"use client";

import { RiHeartFill } from "@remixicon/react";
import type { ProcessedSet, Review } from "@workspace/types";
import { Card, CardContent } from "@workspace/ui/components/card";
import { cn } from "@workspace/ui/lib/utils";
import { memo } from "react";
import { SetInfoDisplay } from "@/components/shared/set-info-display";
import { StatusBadge } from "@/components/shared/status-badge";
import { isSetPast } from "@/lib/utils/date/formatting";
import {
	renderArtistName,
	renderStarRating,
} from "@/lib/utils/set-card-helpers";

interface MyListCardProps {
	review: Review;
	set: ProcessedSet | undefined;
	savedSchedule: string[];
	onStartReview: (setId: string) => void;
}

const MyListCard = memo(function MyListCard({
	review,
	set,
	savedSchedule,
	onStartReview,
}: MyListCardProps) {
	// If the set data is missing for some reason, don't render the card
	if (!set) {
		return null;
	}

	return (
		<Card
			key={review.setId} // Key moved here from the map in parent
			className={cn(
				"relative w-full cursor-pointer overflow-hidden rounded-xl transition-all duration-150",
				"hover:bg-muted/50 dark:hover:bg-muted/20",
				"active:scale-[0.99] active:bg-muted dark:active:bg-muted/40",
			)}
			onClick={() => onStartReview(review.setId)}
		>
			<CardContent className="p-3">
				<div className="flex flex-col gap-3">
					{/* Top section: Artist photo, info and badges */}
					<div className="flex items-start justify-between gap-3">
						<div className="min-w-0 flex-1">
							<SetInfoDisplay
								set={set}
								titleElement="h3"
								context="card"
								renderTitle={(set) =>
									renderArtistName({ set, textBreaking: "break-words" })
								}
								showStage={true}
								showTime={true}
								photoType="artist"
								photoSize="lg"
							/>
						</div>

						{/* Status badges */}
						<div className="flex flex-col items-end gap-3">
							{review.mustSee && (
								<StatusBadge status="mustSee" size="responsive" compactLabel="Must" />
							)}
							{/* Check for Want to See: not mustSee, not saw, and in savedSchedule */}
							{!review.mustSee &&
								!review.sawLive &&
								!review.sawStream &&
								savedSchedule.includes(review.setId) && (
									<StatusBadge
										status="wantToSee"
										size="responsive"
										label={isSetPast(set) ? "Wanted to See" : "Want to See"}
										compactLabel={isSetPast(set) ? "Wanted" : "Want"}
									/>
								)}
							{review.sawLive && (
								<StatusBadge status="sawLive" size="responsive" compactLabel="Live" />
							)}
							{review.sawStream && !review.sawLive && (
								<StatusBadge status="sawStream" size="responsive" compactLabel="Stream" />
							)}
						</div>
					</div>

					{/* Star rating and heart - positioned above text content */}
					{(review.rating > 0 || review.loved) && (
						<div className="flex items-center">
							{review.rating > 0 && (
								<div className="flex items-center">
									{renderStarRating({ rating: review.rating, size: "lg" })}
								</div>
							)}

							{/* Heart if loved - positioned right after stars */}
							{review.loved && (
								<div className={cn(review.rating > 0 ? "ml-2" : "")}>
									<RiHeartFill className="h-5 w-5 text-red-500" />
								</div>
							)}
						</div>
					)}

					{/* Full-width text content */}
					{review.content && (
						<div className="w-full">
							<p className="line-clamp-3 text-sm">{review.content}</p>
						</div>
					)}
				</div>
			</CardContent>
		</Card>
	);
});

MyListCard.displayName = "MyListCard";

export { MyListCard };
